import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

function normalizeAccount(account) {
  if (!account?.ok) return null;

  const username = account.currentOperator || 'operateur';
  const role = account.role || (account.admin ? 'super_admin' : 'support_n1');

  return {
    sub: username,
    username,
    role,
    roleLabel: account.roleLabel || role,
    permissions: Array.isArray(account.permissions) ? account.permissions : [],
    profiles: account.profiles || {},
    phoneNumber: username,
    isAdmin: account.admin === true,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deviceConflict, setDeviceConflict] = useState(false);

  const refreshAccount = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/v2/api/account', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        setUser(null);
        return null;
      }

      const account = await response.json();
      const normalized = normalizeAccount(account);
      setUser(normalized);
      return normalized;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAccount();
  }, [refreshAccount]);

  const login = useCallback(async (username, password) => {
    const body = new URLSearchParams();
    body.set('username', String(username || '').trim());
    body.set('password', String(password || ''));

    const response = await fetch('/v2/api/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });

    if (!response.ok) {
      throw new Error('Identifiant ou mot de passe incorrect.');
    }

    const account = await refreshAccount();
    if (!account) {
      throw new Error('Session non active apres connexion. Rechargez la page et reessayez.');
    }
    return account;
  }, [refreshAccount]);

  const logout = useCallback(() => {
    setUser(null);
    setDeviceConflict(false);
    window.location.href = '/logout';
  }, []);

  const value = {
    user,
    loading,
    deviceConflict,
    isAuthenticated: !!user,
    login,
    logout,
    refreshAccount,
    isTokenExpired: () => false,
    clearConflict: () => setDeviceConflict(false),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
